import React, { useState, useCallback, useEffect } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { useTranslation } from 'localization';
import { useSchemaType } from '../../../../hooks/useSchemaType';
import type { SphereStatusPoint } from 'types/global';
import type { VelocityBucketKey } from 'types/sphereMetrics';
import type { PermissionList } from '../../../../../types/sharedTypes';
import ButtonAtom from '../../../atoms/Button/ButtonAtom';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import { FactorData, PanelMode } from '../StatusPopover/StatusFactorPanel';
import {
  buildShowOnCardsLookup,
  isFactorOnCard,
  resolveFactorDisplayName,
} from '../../../../utils/factorDisplay';
import './MobileStatusDrawerMolecule.scss';

interface MobileStatusDrawerProps {
  visible: boolean;
  onHide: () => void;
  sphereId: string;
  sphereName: string;
  schemaType?: string;
  status?: SphereStatusPoint;
  onStatusDeployed?: () => void;
  onStatusRefreshed?: (newStatus: SphereStatusPoint) => void;
  /** Callback when user wants to configure status factors - opens SphereEditor to Status Factors tab */
  onConfigureFactors?: (sphereId: string) => void;
  /** Function to fetch permission list - passed from parent for edit permission checks */
  fetchPermissionList?: (
    sphereId: string,
    nodeLabel: string,
    permissions?: string[]
  ) => Promise<PermissionList | null>;
}

/**
 * MobileStatusDrawerMolecule - Bottom drawer for mobile status details
 * Displays comprehensive status information with factor breakdown
 */
const MobileStatusDrawerMolecule: React.FC<MobileStatusDrawerProps> = ({
  visible,
  onHide,
  sphereId,
  sphereName,
  schemaType,
  status,
  onStatusDeployed,
  onStatusRefreshed,
  onConfigureFactors,
  fetchPermissionList,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [localStatus, setLocalStatus] = useState<SphereStatusPoint | undefined>(undefined);
  const [hasEditPermission, setHasEditPermission] = useState<boolean | null>(null);

  // Slide-out panel state
  const [selectedFactor, setSelectedFactor] = useState<FactorData | null>(null);
  const [panelMode, setPanelMode] = useState<PanelMode>('history');
  const [panelLoading, setPanelLoading] = useState(false);

  // Use local status if available (after refresh), otherwise use prop
  const effectiveStatus = localStatus || status;

  // Reset local status and permission when sphere or visibility changes
  useEffect(() => {
    if (visible) {
      setLocalStatus(undefined);
      setSelectedFactor(null);
      setPanelLoading(false);
    }
  }, [sphereId, visible]);

  // Check edit permission when drawer becomes visible
  useEffect(() => {
    if (visible && fetchPermissionList && hasEditPermission === null) {
      (async () => {
        try {
          const permissionList = await fetchPermissionList(sphereId, sphereName, ['edit']);
          const canEdit = permissionList?.permissions?.edit ?? false;
          setHasEditPermission(canEdit);
        } catch (error) {
          console.error('[MobileStatusDrawer] Failed to check permissions:', error);
          setHasEditPermission(false);
        }
      })();
    }
  }, [visible, fetchPermissionList, sphereId, sphereName, hasEditPermission]);

  const { schema: schemaDefinition } = useSchemaType(schemaType ?? null);
  const statusFactorsConfig = schemaDefinition?.statusFactors;

  // Check if schema has status factors defined
  const schemaHasStatusFactors = statusFactorsConfig?.enabled === true &&
    (statusFactorsConfig?.factors?.length || 0) > 0;

  const schemaFactorCount = statusFactorsConfig?.factors?.length || 0;
  const schemaFactors = statusFactorsConfig?.factors || [];

  // Localized fallback label for a factor whose owner never set a name.
  const getFactorTypeLabel = (factorType: string): string => {
    const key = `statusPopover.factorNames.${factorType}`;
    const translated = t(key);
    return translated === key ? factorType.replaceAll('_', ' ') : translated;
  };

  const getFactorDescription = (factorType: string): string => {
    const key = `statusPopover.factorDescriptions.${factorType}`;
    const translated = t(key);
    return translated === key ? t('statusPopover.factorDescriptions.custom') : translated;
  };

  const getStatusLabel = (statusVal: string): string => {
    const key = `statusPopover.statusValues.${statusVal}`;
    const translated = t(key);
    return translated === key ? statusVal : translated;
  };

  const getSchemaFactorDetails = (factor: any): { name: string; description: string } => {
    const config = factor.config || {};
    const factorType = factor.factorType;

    if (factorType === 'related_spheres') {
      const monitoringRules = config.monitoringRules || {};
      const perspectiveRel = monitoringRules.perspectiveRelationship;
      const sphereFilters = monitoringRules.sphereFilters?.propertyFilters || [];
      const aggregationMethod = config.aggregationMethod || 'worst';
      const emptyBehavior = config.emptyBehavior;

      let relationshipDesc = t('statusPopover.factorDetails.childSpheres');
      if (perspectiveRel) {
        const perspectiveName = perspectiveRel.perspectiveSystemName?.replace('center_', '').replaceAll('_', ' ');
        if (perspectiveRel.direction === 'outbound') {
          relationshipDesc = t('statusPopover.factorDetails.spheresWhereThisIs', { perspective: perspectiveName });
        } else {
          relationshipDesc = t('statusPopover.factorDetails.spheresAssignedAs', { perspective: perspectiveName });
        }
      }

      let filterDesc = '';
      if (sphereFilters.length > 0) {
        const filterParts = sphereFilters.map((f: any) => {
          const opMap: Record<string, string> = {
            'gte': '>=', 'gt': '>', 'lte': '<=', 'lt': '<', 'equals': '=', 'ne': '!='
          };
          return `${f.property} ${opMap[f.operator] || f.operator} ${f.value}`;
        });
        filterDesc = t('statusPopover.factorDetails.filteredBy', { filters: filterParts.join(', ') });
      }

      const aggDesc = t(`statusPopover.factorDetails.aggregation.${aggregationMethod}`) || aggregationMethod;

      let emptyDesc = '';
      if (emptyBehavior === 'green') {
        emptyDesc = t('statusPopover.factorDetails.emptyBehavior.green');
      } else if (emptyBehavior === 'yellow') {
        emptyDesc = t('statusPopover.factorDetails.emptyBehavior.yellow');
      } else if (emptyBehavior === 'red') {
        emptyDesc = t('statusPopover.factorDetails.emptyBehavior.red');
      }

      const descriptionParts = [relationshipDesc, filterDesc, aggDesc, emptyDesc].filter(Boolean);

      return {
        name: t('statusPopover.factorNames.related_spheres'),
        description: descriptionParts.join('. ')
      };
    }

    const nameKey = `statusPopover.factorNames.${factorType}`;
    const descKey = `statusPopover.factorDescriptions.${factorType}`;
    return {
      name: t(nameKey) === nameKey ? factorType.replaceAll('_', ' ') : t(nameKey),
      description: t(descKey) === descKey ? t('statusPopover.factorDescriptions.custom') : t(descKey)
    };
  };

  // Deploy status factors from schema
  const handleDeployFactors = useCallback(async () => {
    if (!apiClients?.graphClient || !sphereId) return;

    setIsDeploying(true);
    setDeployError(null);

    try {
      await apiClients.graphClient.post(`/sphere/${sphereId}/status/deploy-factors`, {
        recalculate: true
      });
      onStatusDeployed?.();
    } catch (err: any) {
      console.error('Failed to deploy status factors:', err);
      setDeployError(err.message || t('statusPopover.deployError'));
    } finally {
      setIsDeploying(false);
    }
  }, [apiClients, sphereId, onStatusDeployed, t]);

  // Refresh/recalculate status manually
  const handleRefresh = useCallback(async () => {
    if (!apiClients?.graphClient || !sphereId) return;

    setIsRefreshing(true);

    try {
      const data = await apiClients.graphClient.post(`/sphere/${sphereId}/points/status/recalculate`, {
        invalidateCache: true
      });

      if (data?.statusPoint) {
        setLocalStatus(data.statusPoint);
      }

      if (data && onStatusRefreshed) {
        onStatusRefreshed(data.statusPoint || data);
      }
    } catch (err: any) {
      console.error('Failed to recalculate status:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, [apiClients, sphereId, onStatusRefreshed]);

  // Handle opening factor panel
  const handleFactorClick = useCallback((factor: FactorData, mode: PanelMode) => {
    console.log('[MobileStatusDrawer] Opening factor panel:', factor.name, mode);
    setSelectedFactor(factor);
    setPanelMode(mode);
  }, []);

  // Handle closing factor panel
  const handlePanelClose = useCallback(() => {
    setSelectedFactor(null);
    setPanelMode('history');
  }, []);



  // Render content based on status state
  const renderContent = () => {
    // No status point configured at all
    if (!effectiveStatus) {
      if (schemaHasStatusFactors) {
        return (
          <div className="mobile-status-drawer__deploy-state">
            <div className="deploy-state-icon">🚀</div>
            <h4>{t('statusPopover.schemaFactorsAvailable.title')}</h4>
            <p>{t('statusPopover.schemaFactorsAvailable.description', { count: schemaFactorCount })}</p>
            <ul className="mobile-status-drawer__factor-list mobile-status-drawer__factor-list--preview">
              {schemaFactors.map((factor: any, index: number) => {
                const details = getSchemaFactorDetails(factor);
                const factorKey = `${factor.factorType}-${index}`;
                return (
                  <li key={factorKey} className="mobile-status-drawer__factor-item">
                    <div className="mobile-status-drawer__factor-header">
                      <span className="mobile-status-drawer__factor-name">{details.name}</span>
                      <span className="mobile-status-drawer__factor-weight">
                        {t('statusPopover.weight')}: {factor.weight}
                      </span>
                    </div>
                    <div className="mobile-status-drawer__factor-description">{details.description}</div>
                  </li>
                );
              })}
            </ul>
            {deployError && (
              <div className="mobile-status-drawer__deploy-error">
                <small>{deployError}</small>
              </div>
            )}
            <ButtonAtom
              label={isDeploying
                ? t('statusPopover.schemaFactorsAvailable.deployingButton')
                : t('statusPopover.schemaFactorsAvailable.deployButton')
              }
              icon={isDeploying ? 'pi pi-spin pi-spinner' : 'pi pi-bolt'}
              onClick={handleDeployFactors}
              disabled={isDeploying || !apiClients}
              variant="primary"
              shape="pill"
            />
            <p className="mobile-status-drawer__note">
              <small>{t('statusPopover.schemaFactorsAvailable.note')}</small>
            </p>
          </div>
        );
      }

      return (
        <div className="mobile-status-drawer__empty-state">
          <div className="empty-state-icon">📊</div>
          <h4>{t('statusPopover.noStatusTracking.title')}</h4>
          <p>{t('statusPopover.noStatusTracking.description')}</p>
          <ButtonAtom
            label={t('statusPopover.noStatusTracking.configureButton')}
            icon="pi pi-cog"
            disabled
            variant="secondary"
            shape="pill"
          />
          <p className="mobile-status-drawer__note">
            <small>{t('statusPopover.noStatusTracking.comingSoon')}</small>
          </p>
        </div>
      );
    }

    // Status point exists - extract data
    const { currentValue, computedAt, factorDefinitions } = effectiveStatus;
    const factorCount = factorDefinitions?.length || 0;
    const deployedFactorCount = factorDefinitions?.filter(f => f.deployed).length || 0;
    const currentValueFactorCount = currentValue?.factors?.length || 0;
    const hasFactors = factorCount > 0 || currentValueFactorCount > 0;
    const hasDeployedFactors = deployedFactorCount > 0 || currentValueFactorCount > 0;
    const statusValue = currentValue?.status || 'unknown';
    const hasManualFactors = factorDefinitions?.some(f => f.deployed && f.factorType === 'manual') ||
      currentValue?.factors?.some(f => f.factorType === 'manual') || false;
    const isStatusComputed = statusValue !== 'unknown' || hasManualFactors;

    // No factors
    if (!hasFactors) {
      if (schemaHasStatusFactors) {
        return (
          <div className="mobile-status-drawer__deploy-state">
            <div className="deploy-state-icon">🚀</div>
            <h4>{t('statusPopover.schemaFactorsAvailable.title')}</h4>
            <p>{t('statusPopover.schemaFactorsAvailable.description', { count: schemaFactorCount })}</p>
            <ul className="mobile-status-drawer__factor-list mobile-status-drawer__factor-list--preview">
              {schemaFactors.map((factor: any, index: number) => {
                const details = getSchemaFactorDetails(factor);
                const factorKey = `${factor.factorType}-${index}`;
                return (
                  <li key={factorKey} className="mobile-status-drawer__factor-item">
                    <div className="mobile-status-drawer__factor-header">
                      <span className="mobile-status-drawer__factor-name">{details.name}</span>
                      <span className="mobile-status-drawer__factor-weight">
                        {t('statusPopover.weight')}: {factor.weight}
                      </span>
                    </div>
                    <div className="mobile-status-drawer__factor-description">{details.description}</div>
                  </li>
                );
              })}
            </ul>
            {deployError && (
              <div className="mobile-status-drawer__deploy-error">
                <small>{deployError}</small>
              </div>
            )}
            <ButtonAtom
              label={isDeploying
                ? t('statusPopover.schemaFactorsAvailable.deployingButton')
                : t('statusPopover.schemaFactorsAvailable.deployButton')
              }
              icon={isDeploying ? 'pi pi-spin pi-spinner' : 'pi pi-bolt'}
              onClick={handleDeployFactors}
              disabled={isDeploying || !apiClients}
              variant="primary"
              shape="pill"
            />
            <p className="mobile-status-drawer__note">
              <small>{t('statusPopover.schemaFactorsAvailable.note')}</small>
            </p>
          </div>
        );
      }

      return (
        <div className="mobile-status-drawer__empty-state">
          <div className="empty-state-icon">📊</div>
          <h4>{t('statusPopover.noFactors.title')}</h4>
          <p>{t('statusPopover.noFactors.description')}</p>
          <p className="mobile-status-drawer__note">
            <small>{t('statusPopover.noFactors.comingSoon')}</small>
          </p>
        </div>
      );
    }

    // Has factors but none deployed
    if (!hasDeployedFactors) {
      return (
        <div className="mobile-status-drawer__draft-state">
          <div className="draft-state-icon">📝</div>
          <h4>{t('statusPopover.notDeployed.title')}</h4>
          <p>{t('statusPopover.notDeployed.description', { count: factorCount })}</p>
          <p className="mobile-status-drawer__note">
            <small>{t('statusPopover.notDeployed.comingSoon')}</small>
          </p>
        </div>
      );
    }

    // Status has been computed - show factor breakdown
    if (isStatusComputed) {
      // EAINT-750: mirror StatusPopoverMolecule via the shared helper.
      const definitionsById = buildShowOnCardsLookup(factorDefinitions);
      const displayFactors = currentValue.factors?.length > 0
        ? currentValue.factors.filter(f => isFactorOnCard(f, definitionsById))
        : factorDefinitions?.filter(f => f.deployed && isFactorOnCard(f, definitionsById));

      return (
        <>
          {/* Factor List */}
          <div className="mobile-status-drawer__factors">
            <div className="mobile-status-drawer__factors-header">
              <h4 className="factors-title">{t('statusPopover.computed.title')}</h4>
            </div>
            <p className="factors-description">{t('statusPopover.computed.description')}</p>
            <div className="factors-list-wrapper">

            <ul className="mobile-status-drawer__factor-list">
              {displayFactors?.map(factor => {
                const factorStatus = factor.status || factor.currentStatus || 'unknown';
                const factorStatusLabel = getStatusLabel(factorStatus);

                const factorData: FactorData = {
                  id: factor.id,
                  factorType: factor.factorType,
                  // Shared resolver reads summary.name AND metadata.name so the
                  // same call works whether `factor` is a summary or a factorDefinition.
                  name: resolveFactorDisplayName(
                    factor as { name?: string; metadata?: { name?: string } },
                    getFactorTypeLabel(factor.factorType),
                  ),
                  description: getFactorDescription(factor.factorType),
                  weight: factor.weight,
                  status: factorStatus,
                  statusLabel: factorStatusLabel,
                  velocityScore: factor.velocityScore,
                  velocityBucket: factor.velocityBucket as VelocityBucketKey,
                  commentRequired: factor.commentRequired,
                  minCommentLength: factor.minCommentLength,
                  scheduleBroken: (factor as { metadata?: { scheduleBroken?: { code: string; message: string; detectedAt: string } } })
                    .metadata?.scheduleBroken,
                };

                return (
                  <li key={factor.id} className="mobile-status-drawer__factor-item">
                    <button
                      type="button"
                      className="mobile-status-drawer__factor-item--clickable"
                      onClick={() => handleFactorClick(factorData, 'history')}
                    >
                      <div className="mobile-status-drawer__factor-header">
                        <span className="mobile-status-drawer__factor-name">{factorData.name}</span>
                        <span className="mobile-status-drawer__factor-weight">
                          {t('statusPopover.weight')}: {factor.weight}
                        </span>
                      </div>
                      <div className={`mobile-status-drawer__factor-status-value mobile-status-drawer__factor-status-value--${factorStatus.replaceAll('_', '-')}`}>
                        {factorStatusLabel}
                      </div>
                      <div className="mobile-status-drawer__factor-description">{factorData.description}</div>
                      <i className="pi pi-chevron-right mobile-status-drawer__factor-chevron" />
                    </button>
                  </li>
                );
              })}
            </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="mobile-status-drawer__footer">
            <small>{t('statusPopover.lastComputed')}: {new Date(computedAt).toLocaleString()}</small>
          </div>
        </>
      );
    }

    // Has deployed factors but status not computed yet
    return (
      <div className="mobile-status-drawer__draft-state">
        <div className="draft-state-icon">⏳</div>
        <h4>{t('statusPopover.waitingComputation.title')}</h4>
        <p>{t('statusPopover.waitingComputation.description')}</p>
        <p className="mobile-status-drawer__note">
          <small>{t('statusPopover.waitingComputation.note')}</small>
        </p>
      </div>
    );
  };

  // Get status values for header badges
  const currentValue = effectiveStatus?.currentValue;
  const statusValue = currentValue?.status || 'unknown';
  const statusLabel = getStatusLabel(statusValue);
  const velocity = currentValue?.velocity;

  return (
    <Sidebar
      visible={visible}
      position="bottom"
      onHide={onHide}
      className="mobile-status-drawer"
      dismissable
      showCloseIcon={false}
      modal
    >
      <div className="mobile-status-drawer__container">
        {/* Drawer Handle */}
        <div className="mobile-status-drawer__handle" />

        {/* Drawer Header */}
        <div className="mobile-status-drawer__header">
          <h2 className="mobile-status-drawer__title">
            {t('common.status')} - {sphereName}
          </h2>
          <Button
            icon="pi pi-times"
            className="p-button-text p-button-rounded"
            onClick={onHide}
            aria-label={t('common.close')}
          />
        </div>

        {/* Status Header Badges - Always show if status exists */}
        {effectiveStatus && (
          <div className="mobile-status-drawer__header-badges-container">
            <div className="mobile-status-drawer__header-badges">
              <div className={`status-badge-chip status-badge-chip--status status-badge-chip--status-${statusValue.replaceAll('_', '-')}`}>
                <span className="badge-label">{t('statusPopover.statusLabel')}:</span>
                <span className="badge-value">{statusLabel}</span>
              </div>
              <div className="status-badge-chip status-badge-chip--velocity">
                <span className="badge-label">{t('velocity.label')}:</span>
                <span className="badge-value">
                  {velocity ? t(`velocity.buckets.${velocity.bucket}`) : t('velocity.unknown', 'Unknown')}
                </span>
              </div>
              <Button
                icon={isRefreshing ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'}
                className="p-button-text p-button-rounded p-button-sm"
                onClick={handleRefresh}
                disabled={isRefreshing || !apiClients}
                tooltip={t('statusPopover.refresh.tooltip')}
              />
            </div>
          </div>
        )}

        {/* Drawer Content */}
        <div className="mobile-status-drawer__content">
          {/* Show factor details if selected, otherwise show main content */}
          {selectedFactor ? (
            <div className="mobile-status-drawer__factor-details">
              {/* Factor Details Header */}
              <div className="mobile-status-drawer__factor-details-header">
                <button
                  className="mobile-status-drawer__back-btn"
                  onClick={handlePanelClose}
                  disabled={panelLoading}
                >
                  <i className="pi pi-arrow-left" />
                </button>
                <h3 className="mobile-status-drawer__factor-details-title">
                  {selectedFactor.name}
                </h3>
              </div>

              {/* Factor Details Content */}
              <div className="mobile-status-drawer__factor-details-content">
                {selectedFactor.factorType === 'manual' ? (
                  <p className="mobile-status-drawer__factor-info-text">
                    {t('statusPopover.panel.manualFactor', 'This factor is updated through manual check-ins.')}
                  </p>
                ) : (
                  <p className="mobile-status-drawer__factor-info-text">
                    {t('statusPopover.panel.autoCalculated', 'This factor is automatically calculated based on related data.')}
                  </p>
                )}

                {/* Check-in form for manual factors with edit permission */}
                {selectedFactor.factorType === 'manual' && hasEditPermission && panelMode === 'checkin' && (
                  <div className="mobile-status-drawer__checkin-form">
                    <p>{t('statusPopover.checkIn.formDescription', 'Submit a new status check-in for this factor.')}</p>
                    {/* Add check-in form here if needed */}
                  </div>
                )}

                {/* Check-in history for manual factors */}
                {selectedFactor.factorType === 'manual' && panelMode === 'history' && (
                  <div className="mobile-status-drawer__checkin-history">
                    <h4>{t('statusPopover.history.title', 'Check-In History')}</h4>
                    {hasEditPermission && (
                      <button
                        className="mobile-status-drawer__checkin-btn-primary"
                        onClick={() => setPanelMode('checkin')}
                      >
                        <i className="pi pi-pencil" />
                        {t('statusPopover.checkIn.checkInButton', 'Check In')}
                      </button>
                    )}
                    <p className="mobile-status-drawer__history-note">
                      {t('statusPopover.history.loading', 'Loading check-in history...')}
                    </p>
                  </div>
                )}
              </div>

              {/* Factor Details Footer */}
              <div className="mobile-status-drawer__factor-details-footer">
                <small>{t('statusPopover.panel.footerHint', 'Check-ins update the factor status')}</small>
              </div>
            </div>
          ) : (
            renderContent()
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default MobileStatusDrawerMolecule;
